// The nutrition-target arithmetic behind the nutrition plan card and the two screens that open the
// nutrition editor (intake form, program settings). It lives apart from the card component so that
// nothing here imports Angular: it can be used and unit-tested as plain functions. Keep it that way --
// `formatMeasure` comes from `@cf-shared/measure-format` rather than `util/formatting` for the same
// reason, because that file reaches the translation and settings services.
import { DeclaredState, MacroTargetsType, ImmutableState, FitnessState, usesCalorieCycling } from '@cf-shared/data-model/user-state';
import { FitnessPlan } from '@cf-shared/data-model/fitness-plan';
import { ScheduleRule, generateScheduleDaysFromRules, Schedule_ItemType, ScheduleRuleRepeat, isScheduleItemMeal } from '@cf-shared/data-model/schedule';
import { PlanUpdate } from '@cf-shared/plan-update';
import { PlanUpdateOutcome } from '@cf-shared/plan-update-interface';
import { deepClone } from '@cf-shared/deep-helpers';
import { INFOODS, NutrientTargets } from '@cf-shared/data-model/food';
import { IntakeFormState } from '@cf-shared/data-model/intake-form-state';
import { DateTime } from 'luxon';
import { formatMeasure } from '@cf-shared/measure-format';
import { LocaleSettings } from '@cf-shared/shared-util';

export interface MacroData {
  value: string;
  suffix?: 'min' | 'max';
}

export interface MacroSet {
  protein: string | MacroData;
  carbs: string | MacroData;
  fat: string | MacroData;
  fiber: string | MacroData;
}

export interface NutritionPlanData extends MacroSet {
  dailyAvg: string;
  averageMeal?: string;
  cutMeal?: string;
  bulkMeal?: string;
  isCyclingEnabled?: boolean;
  isFlexibleNutrition?: boolean;
  // bulk/cut meal macros (when cycling enabled)
  bulkMealMacros?: MacroSet;
  cutMealMacros?: MacroSet;
  // fixed meal macros (when cycling disabled)
  fixedMealMacros?: MacroSet;
}

export interface ComputedMacroTargets {
  dailyAverage: NutrientTargets | null;
  bulkMealTargets?: NutrientTargets;
  cutMealTargets?: NutrientTargets;
  fixedMealTargets?: NutrientTargets;
}

/**
 * Just enough of `LoggingService` to report a failed solve. The three exported functions below are
 * plain functions with no dependency injection, so the page that calls them passes its own
 * `this.logging` in. Declaring the shape here rather than importing the service keeps this module
 * free of Angular's injector, the same way `coach-dashboard-helpers.ts` takes its `reporter`.
 */
export interface NutritionSolveProblemReporter {
  trackSilentProblem(problemName: string, context: any): void;
}

export interface ComputeNutritionPlanDataParams {
  fitnessPlan: FitnessPlan;
  declaredState: DeclaredState;
  immutableState: ImmutableState;
  fitnessState: FitnessState;
  scheduleRules: ScheduleRule[];
  intakeFormState?: IntakeFormState;
  today: DateTime;
  locale: LocaleSettings;
  /** Where a failed solve is reported. Optional only so a test can leave it out — every caller
   *  in the app passes one, and without it a crash inside the solver is invisible. */
  reporter?: NutritionSolveProblemReporter;
}

/**
 * Extracts raw macro gram values from daily-average NutrientTargets.
 * Fixed mode reads .target, flexible mode reads .min.
 */
export function extractMacroGramsFromTargets(
  dailyAverage: NutrientTargets | null,
  isFlexible: boolean
): { protein: number; carbs: number; fat: number; fiber: number } {
  const getValue = (nutrient: string) => {
    const t = dailyAverage?.[nutrient];
    if (!t) return 0;
    return Math.round(isFlexible ? (t.min ?? t.target ?? 0) : (t.target ?? t.min ?? 0));
  };
  return {
    protein: getValue(INFOODS.Protein),
    carbs: getValue(INFOODS.CarbNet),
    fat: getValue(INFOODS.Fat),
    fiber: getValue(INFOODS.Fiber),
  };
}

/** What `computeRecommendedNutrition` answers with when it HAS a recommendation. */
export interface RecommendedNutrition {
  dailyIntake: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
}

/**
 * Computes the recommended (no-override) nutrition values by re-solving the plan
 * with overrides removed and the baseline nominal restored.
 *
 * Returns `null` when there is no recommendation to give, and every caller must treat that as "we
 * cannot tell you what we recommend" rather than as a zero. The numbers are read back off a plan
 * the solver MUTATES IN PLACE, so on a failed or incomplete solve they are whatever the plan held
 * beforehand -- `null` is what makes the compiler ask. Why it is a `null` rather than a flag, and
 * what the two screens do with it: `design-docs/nutrition-target-persistence.md`.
 *
 * `reporter` is optional only so a test can leave it out; every caller in the app passes one, and
 * without it a crash inside the solver is invisible.
 */
export function computeRecommendedNutrition(
  fitnessPlan: FitnessPlan,
  declaredState: DeclaredState,
  immutableState: ImmutableState,
  fitnessState: FitnessState,
  scheduleRules: ScheduleRule[],
  today: DateTime,
  reporter?: NutritionSolveProblemReporter,
): RecommendedNutrition | null {
  const plan = deepClone(fitnessPlan);
  delete plan.overrideMacros;
  delete plan.overrideDailyAverageIntake;

  // restore the pre-override nominal so the solver computes original targets
  if (plan.baselineNominalDailyAverageIntake != null) {
    plan.nominalDailyAverageIntake = plan.baselineNominalDailyAverageIntake;
  }

  // re-solve to recompute nutrition targets from baseline
  const solver = new PlanUpdate();
  const solveResult = solver.solve({
    immutableState,
    declaredState,
    fitnessState,
    fitnessPlan: plan,
    schedule: undefined,
    weighIns: [],
    historyLogs: [],
    today_local: today.toISO(),
  });

  // if the solve failed, give up here rather than reading numbers back off the plan it was
  // half-way through rewriting. Returning early also keeps computeMacroTargets below from
  // generating a schedule over a plan that may now hold NaN, which would either report the same
  // root cause a second time under a different name or, worse, succeed and produce plausible
  // rubbish.
  if (solveResult.outcome === PlanUpdateOutcome.Failed) {
    reporter?.trackSilentProblem('plan-update-solve-failed', { error: solveResult.error, where: 'computeRecommendedNutrition' });
    return null;
  }

  const isFlexible = declaredState.macroTargets === MacroTargetsType.Flexible;
  const targets = computeMacroTargets(plan, declaredState, immutableState, fitnessState, scheduleRules, today, reporter);

  // no daily average means computeMacroTargets could not work the targets out at all, and
  // extractMacroGramsFromTargets would turn that into four zeros -- which read as a recommendation
  // to eat nothing. computeMacroTargets has already reported whichever path it took.
  if (targets.dailyAverage === null) {
    return null;
  }

  // a solve can report success and still leave no calorie target: the path that runs when we have
  // no usable bodyweight or body-fat reading skips the nutrition pass entirely (see solve() in
  // plan-update.ts). Zero, a negative number and NaN are all "we do not have one" -- and note that
  // the `?? 0` this replaced did NOT catch NaN, because `NaN ?? 0` is NaN.
  const dailyIntake = plan.actualDailyAverageIntake;
  if (!Number.isFinite(dailyIntake) || dailyIntake <= 0) {
    reporter?.trackSilentProblem('plan-update-solve-gave-no-daily-intake', { dailyIntake, where: 'computeRecommendedNutrition' });
    return null;
  }

  return {
    dailyIntake,
    ...extractMacroGramsFromTargets(targets.dailyAverage, isFlexible),
  };
}

/** Both sets of macro grams `EditNutritionPlanPage` is opened with. */
export interface EditNutritionPlanMacros {
  /** what the user's targets are today, with their own overrides applied */
  current: { protein: number; carbs: number; fat: number; fiber: number };
  /** what we would recommend instead, with their overrides taken off */
  recommended: RecommendedNutrition;
}

/**
 * Works out the two sets of numbers `EditNutritionPlanPage` needs, and answers `null` when either
 * of them is missing -- which means the editor must not be opened at all, because it decides which
 * of the user's typed values count as their own overrides by comparing them against these numbers,
 * and it SAVES what it decides.
 *
 * Both pages that open that editor need exactly this and must refuse in exactly the same cases, so
 * the guard lives here rather than being written out twice. What each screen then shows:
 * `design-docs/nutrition-target-persistence.md`.
 */
export function computeEditNutritionPlanMacros(
  fitnessPlan: FitnessPlan,
  declaredState: DeclaredState,
  immutableState: ImmutableState,
  fitnessState: FitnessState,
  scheduleRules: ScheduleRule[],
  today: DateTime,
  isFlexible: boolean,
  reporter?: NutritionSolveProblemReporter,
): EditNutritionPlanMacros | null {
  // clone for the current-targets pass to avoid mutating the live plan; computeRecommendedNutrition
  // clones for itself
  const currentTargets = computeMacroTargets(
    deepClone(fitnessPlan), declaredState, immutableState, fitnessState, scheduleRules, today, reporter);
  if (currentTargets.dailyAverage === null) {
    return null;
  }

  const recommended = computeRecommendedNutrition(
    fitnessPlan, declaredState, immutableState, fitnessState, scheduleRules, today, reporter);
  if (recommended === null) {
    return null;
  }

  return {
    current: extractMacroGramsFromTargets(currentTargets.dailyAverage, isFlexible),
    recommended,
  };
}

/**
 * Computes macro targets from a 7-day schedule.
 * Returns daily averages and meal-specific targets (bulk/cut/fixed).
 *
 * `reporter` is optional only so a test can leave it out; every caller in the app passes one, and
 * without it a crash inside the solver is invisible.
 */
export function computeMacroTargets(
  fitnessPlan: FitnessPlan,
  declaredState: DeclaredState,
  immutableState: ImmutableState,
  fitnessState: FitnessState,
  scheduleRules: ScheduleRule[],
  today: DateTime,
  reporter?: NutritionSolveProblemReporter,
): ComputedMacroTargets {
  const result: ComputedMacroTargets = { dailyAverage: null };

  // filter out one-time rules (ScheduleRuleRepeat.Never)
  const recurringRules = scheduleRules.filter(r => r.repeat !== ScheduleRuleRepeat.Never);

  // generate 7-day schedule starting from today
  const endDate = today.plus({ days: 7 });
  const scheduleDays = generateScheduleDaysFromRules(recurringRules, today, endDate);

  // a user who has a plan always has weekly meal rules -- adjustScheduleToDeclaredState lays them
  // out for all seven weekdays, and both editor screens run it immediately before calling here. So
  // an empty week means a damaged or part-synced schedule, and the caller now turns that into
  // something the user sees. Report it, so it is never a visible refusal with no trace.
  if (scheduleDays.length === 0) {
    reporter?.trackSilentProblem('nutrition-targets-no-recurring-schedule-rules', { ruleCount: scheduleRules.length, where: 'computeMacroTargets' });
    return result;
  }

  // call PlanUpdate.generateSchedule to populate targets
  const planUpdate = new PlanUpdate();
  const scheduleResult = planUpdate.generateSchedule({
    immutableState,
    declaredState,
    fitnessState,
    fitnessPlan,
    schedule: scheduleDays,
    weighIns: [],
    historyLogs: [],
    today_local: today.toISO()
  });

  // a browser console is not somewhere anybody can read in production, so this failure used to go
  // unseen. The early return below is unchanged -- the caller already handles a null daily average.
  if (scheduleResult.outcome === PlanUpdateOutcome.Failed) {
    reporter?.trackSilentProblem('plan-update-generate-schedule-failed', { error: scheduleResult.error, where: 'computeMacroTargets' });
    return result;
  }

  // extract daily targets from DayStart items and compute averages
  const nutrients = [INFOODS.Protein, INFOODS.CarbNet, INFOODS.Fat, INFOODS.Fiber];
  const totals: { [key: string]: { min: number; max: number; target: number; count: number } } = {};

  for (const nutrient of nutrients) {
    totals[nutrient] = { min: 0, max: 0, target: 0, count: 0 };
  }

  // also extract meal targets (bulk/cut/fixed)
  let bulkMeal: NutrientTargets | undefined;
  let cutMeal: NutrientTargets | undefined;
  let fixedMeal: NutrientTargets | undefined;

  // if calorie cycling is active, categorize meals by cutMeal flag;
  // otherwise treat every meal as a fixed meal (PlanUpdate.generateSchedule
  // always sets cutMeal to true/false, even when cycling is off)
  const trainingDaysWeekly = declaredState.trainingDays?.filter(d => d).length ?? 0;
  const calorieCyclingActive = trainingDaysWeekly > 0 && usesCalorieCycling(declaredState);

  for (const day of scheduleResult.schedule) {
    // extract daily targets from DayStart
    const dayStart = day.items.find(item => item.type === Schedule_ItemType.DayStart);
    if (dayStart?.targets) {
      for (const nutrient of nutrients) {
        const t = dayStart.targets[nutrient];
        if (t) {
          if (t.min !== undefined) totals[nutrient].min += t.min;
          if (t.max !== undefined) totals[nutrient].max += t.max;
          if (t.target !== undefined) totals[nutrient].target += t.target;
          totals[nutrient].count++;
        }
      }
    }

    // extract meal targets (find first of each type)
    for (const item of day.items) {
      if (isScheduleItemMeal(item) && item.targets) {
        if (calorieCyclingActive) {
          if (item.cutMeal === true && !cutMeal) {
            cutMeal = item.targets;
          } else if (item.cutMeal === false && !bulkMeal) {
            bulkMeal = item.targets;
          }
        } else if (!fixedMeal) {
          fixedMeal = item.targets;
        }
      }
    }
  }

  // build averaged daily NutrientTargets
  const avgTargets: NutrientTargets = {};
  for (const nutrient of nutrients) {
    const t = totals[nutrient];
    if (t.count > 0) {
      avgTargets[nutrient] = {};
      if (t.min > 0) avgTargets[nutrient].min = t.min / t.count;
      if (t.max > 0) avgTargets[nutrient].max = t.max / t.count;
      if (t.target > 0) avgTargets[nutrient].target = t.target / t.count;
    }
  }

  // no nutrient carried a target on any of the seven days. The schedule generated cleanly, so
  // nothing above has reported anything -- and the caller now turns this into something the user
  // sees, so report it here rather than leaving a visible refusal with no trace.
  if (Object.keys(avgTargets).length === 0) {
    reporter?.trackSilentProblem('nutrition-targets-week-carried-no-targets', { dayCount: scheduleResult.schedule.length, where: 'computeMacroTargets' });
  }

  result.dailyAverage = Object.keys(avgTargets).length > 0 ? avgTargets : null;
  result.bulkMealTargets = bulkMeal;
  result.cutMealTargets = cutMeal;
  result.fixedMealTargets = fixedMeal;

  return result;
}

/**
 * Computes NutritionPlanData from raw fitness plan inputs.
 * Shared utility function for use by program-settings-page and intake-form.
 */
export function computeNutritionPlanData(params: ComputeNutritionPlanDataParams): NutritionPlanData | null {
  const { fitnessPlan, declaredState, immutableState, fitnessState, scheduleRules, intakeFormState, today, locale, reporter } = params;

  // helper to format kcal values (rounded to whole numbers for display)
  const formatKcal = (value: number): string => formatMeasure(Math.round(value), 'nutrient_kcal', locale, 'kcal');

  // helper to format gram values (rounded to whole numbers for display)
  const formatGrams = (value: number): string => formatMeasure(Math.round(value), 'nutrient_g', locale, 'g');

  if (!fitnessPlan?.weeklyTargets) return null;

  // use override value if set, otherwise use actual value
  const dailyIntake = fitnessPlan.overrideDailyAverageIntake ?? fitnessPlan.actualDailyAverageIntake;

  // check if flexible nutrition is enabled (fallback to declaredState if intakeFormState not provided)
  const isFlexible = (intakeFormState?.macroTargets ?? declaredState.macroTargets) === MacroTargetsType.Flexible;

  // check if calorie cycling is enabled (fallback to declaredState if intakeFormState not provided)
  const trainingDays = intakeFormState?.trainingDays ?? declaredState.trainingDays;
  const advancedMethodsCalorieCycling = intakeFormState?.advancedMethodsCalorieCycling ?? usesCalorieCycling(declaredState);
  const calorieCyclingEnabled = trainingDays.filter(e => e === true).length > 0
    && advancedMethodsCalorieCycling;

  const nutrition: NutritionPlanData = {
    dailyAvg: formatKcal(dailyIntake),
    isCyclingEnabled: calorieCyclingEnabled,
    isFlexibleNutrition: isFlexible,
    protein: '-',
    carbs: '-',
    fat: '-',
    fiber: '-'
  };

  // get computed macro targets from schedule generation
  const computedTargets = scheduleRules?.length > 0
    ? computeMacroTargets(fitnessPlan, declaredState, immutableState, fitnessState, scheduleRules, today, reporter)
    : { dailyAverage: null } as ComputedMacroTargets;

  // helper to format macro value with min suffix for flexible nutrition
  const formatMacroFromTargets = (targets: NutrientTargets | undefined, nutrient: string): string | MacroData => {
    const target = targets?.[nutrient];
    const value = target
      ? (isFlexible ? (target.min ?? target.target ?? 0) : (target.target ?? target.min ?? 0))
      : null;
    const displayValue = value != null ? formatGrams(value) : '-';
    if (isFlexible) {
      return { value: displayValue, suffix: 'min' as const };
    }
    return displayValue;
  };

  if (computedTargets.dailyAverage) {
    nutrition.protein = formatMacroFromTargets(computedTargets.dailyAverage, INFOODS.Protein);
    nutrition.carbs = formatMacroFromTargets(computedTargets.dailyAverage, INFOODS.CarbNet);
    nutrition.fat = formatMacroFromTargets(computedTargets.dailyAverage, INFOODS.Fat);
    nutrition.fiber = formatMacroFromTargets(computedTargets.dailyAverage, INFOODS.Fiber);
  }

  // set calorie and macro values based on cycling
  if (calorieCyclingEnabled) {
    // use computed targets for calories
    const cutTargets = computedTargets.cutMealTargets;
    const bulkTargets = computedTargets.bulkMealTargets;

    nutrition.cutMeal = cutTargets?.[INFOODS.Calories]?.target != null
      ? formatKcal(cutTargets[INFOODS.Calories].target)
      : 'N/A';
    nutrition.bulkMeal = bulkTargets?.[INFOODS.Calories]?.target != null
      ? formatKcal(bulkTargets[INFOODS.Calories].target)
      : 'N/A';

    // populate bulk/cut meal macros from computed targets
    if (bulkTargets) {
      nutrition.bulkMealMacros = {
        protein: formatMacroFromTargets(bulkTargets, INFOODS.Protein),
        carbs: formatMacroFromTargets(bulkTargets, INFOODS.CarbNet),
        fat: formatMacroFromTargets(bulkTargets, INFOODS.Fat),
        fiber: formatMacroFromTargets(bulkTargets, INFOODS.Fiber)
      };
    }
    if (cutTargets) {
      nutrition.cutMealMacros = {
        protein: formatMacroFromTargets(cutTargets, INFOODS.Protein),
        carbs: formatMacroFromTargets(cutTargets, INFOODS.CarbNet),
        fat: formatMacroFromTargets(cutTargets, INFOODS.Fat),
        fiber: formatMacroFromTargets(cutTargets, INFOODS.Fiber)
      };
    }
  } else {
    // use computed fixed meal targets when calorie cycling is disabled
    const fixedTargets = computedTargets.fixedMealTargets;

    nutrition.averageMeal = fixedTargets?.[INFOODS.Calories]?.target != null
      ? formatKcal(fixedTargets[INFOODS.Calories].target)
      : 'N/A';

    // populate fixed meal macros for "Per Meal" tab
    if (fixedTargets) {
      nutrition.fixedMealMacros = {
        protein: formatMacroFromTargets(fixedTargets, INFOODS.Protein),
        carbs: formatMacroFromTargets(fixedTargets, INFOODS.CarbNet),
        fat: formatMacroFromTargets(fixedTargets, INFOODS.Fat),
        fiber: formatMacroFromTargets(fixedTargets, INFOODS.Fiber)
      };
    }
  }

  return nutrition;
}
